import { useEffect, useState, useMemo } from 'react';
import {
  Container,
  Box,
  Flex,
  Text,
  SimpleGrid,
  GridItem,
  Tag,
  Link,
  Stack,
  Switch,
  Tooltip,
  Heading,
  List,
  ListItem,
  useTheme,
  RadioGroup,
  Radio,
  VStack,
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  Button,
  HStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import GaugeChart from 'react-gauge-chart';
import { FiChevronDown } from 'react-icons/fi';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { isEmpty } from 'lodash';
import { selectedDatabaseIdState } from '@transport-insights/uikit';
import { isComparativeReportState, selectedOnfCategoriesState, selectedGroupState, selectedOnfCategoryTypeState, selectedOnfStreetFamilyState, selectedCategoriesForGroupSelector } from '@src/modules/onf/state/filtersContext';
import { getContrastColor } from '@src/utils/color';
import MainLayout from '@src/components/layouts/main';
import { useReports } from '@src/modules/onf/context/reports';
import LoadingSpinner from '@src/components/shared/LoadingSpinner';
import EmptyState from '@src/components/shared/EmptyState';
import { useNetworkSelector } from '@src/modules/onf/context/network-selector';
import { reportsList } from '@src/modules/onf/api/reports-list';
import MoreInfoDrawer from '@src/components/shared/MoreInfoDrawer';
import CcdcCoverageIndicator from '@src/components/shared/CcdcCoverageIndicator';
import { useSettingsConfig } from '@src/modules/settings/context/settings-api-hooks';
import ReportHeading from '../components/shared/ReportHeading';
import ReportModal from '../components/shared/ReportModal';
import DataQualityTable from '../components/shared/DataQualityTable';
import NetworkSelector from '../components/shared/NetworkSelector';
import ReportSelector from '../components/shared/ReportSelector';
import MeasureDescription from '../components/shared/MeasureDescription';
import Pagination from '../components/shared/Pagination';
import ReportToggles from '../components/shared/ReportToggles';
import HnoFilter from '../components/shared/HnoFilter';

import { CATEGORY_GROUPS } from '../shared/constants';

export default function ReportView() {
  const theme = useTheme();
  const { data: config } = useSettingsConfig();
  const showOnfSHFilter = config?.performance?.ShowOnfSHFilter;
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const { reportId } = useParams();
  const selectedGroup = useRecoilValue(selectedGroupState);
  const setSelectedGroup = useSetRecoilState(selectedGroupState);
  const setCategories = useSetRecoilState(selectedOnfCategoriesState);
  const selectedCategories = useRecoilValue(selectedCategoriesForGroupSelector);
  const categoryType = useRecoilValue(selectedOnfCategoryTypeState);
  const setCategoryType = useSetRecoilState(selectedOnfCategoryTypeState);
  const hasUrbanOrRuralCategories = CATEGORY_GROUPS.find((g) => g.key === selectedGroup)?.categories?.every((c) => c.streetFamily !== null);
  const hasUrbanAndRuralToggles = CATEGORY_GROUPS.find((g) => g.key === selectedGroup)?.categories?.every((c) => c.streetFamily === null);
  const selectedStreetFamily = useRecoilValue(selectedOnfStreetFamilyState);
  const setSelectedStreetFamily = useSetRecoilState(selectedOnfStreetFamilyState);
  const [params, setParams] = useState({ networks: [], hno: [] });

  const { data: rawData, isLoading: isLoadingReport } = useReports(reportId, params);
  const { data: selectorData, isLoading: isLoadingSelectorData } = useNetworkSelector();
  const { relevantLinks, importantLinks, toggles } = reportsList.find((x) => x.id === reportId) || {};
  const [selectedToggleOption, setSelectedToggleOption] = useState(() => Object.fromEntries((toggles || []).map((x) => ([x.key, x.default]))));
  const isLoading = isLoadingReport || isLoadingSelectorData;
  const reportClassifications = [
    ...new Set(
      (rawData?.data ?? [])
        .map((x) => x.classification)
        .filter(Boolean),
    ),
  ];

  window.Apex.noData = {
    text: 'Selected categories have no totals for the year',
  };

  // ensure selectedGroup is valid
  useEffect(() => {
    if (selectedGroup && !CATEGORY_GROUPS.map((g) => g.key).includes(selectedGroup)) {
      setSelectedGroup(CATEGORY_GROUPS[0]?.key ?? '');
    }
  }, [selectedGroup, setSelectedGroup]);

  // determine which categories are present for the selected network in the raw data
  const dataCategories = useMemo(() => {
    if (isEmpty(rawData?.data)) return [];
    return [...new Set(rawData.data.filter((x) => x.id === rcaId).filter((x) => (!hasUrbanOrRuralCategories ? x.streetFamily === selectedStreetFamily : true)).map((x) => x.category))];
  }, [rawData?.data, rcaId, selectedStreetFamily, hasUrbanOrRuralCategories]);

  // whether a category object (from CATEGORY_GROUPS) is disabled for this network/report
  const isCatDisabled = (catObj) => !dataCategories.includes(catObj.description);

  // toggle a category (by description) within a groupKey
  const handleSetCategory = (groupKey, catObj) => {
    setCategories((prev = {}) => {
      const current = prev[groupKey] ?? [];
      const id = catObj.description;
      const updated = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
      return { ...prev, [groupKey]: updated };
    });
  };

  // helper: whether any category is selected across groups
  const anySelected = selectedCategories.length > 0;

  // apply a quick filter (all/urban/rural) across all groups
  const applyQuickFilter = (type) => {
    if (!hasUrbanOrRuralCategories) return;

    // Get the currently selected group
    const group = CATEGORY_GROUPS.find((g) => g.key === selectedGroup);
    if (!group) return;

    // Only consider categories that exist in the data (enabled)
    const validCats = group.categories.filter((c) => dataCategories.includes(c.description));

    let selectedDescriptions = [];
    if (type === 'all') {
      selectedDescriptions = validCats.map((c) => c.description);
    } else if (type === 'urban') {
      selectedDescriptions = validCats
        .filter((c) => c.streetFamily === 'Urban')
        .map((c) => c.description);
    } else if (type === 'rural') {
      selectedDescriptions = validCats
        .filter((c) => c.streetFamily === 'Rural')
        .map((c) => c.description);
    }

    // Merge new selections only into the currently selected group
    setCategories((prev = {}) => ({
      ...prev, // keep existing categories for other groups
      [selectedGroup]: selectedDescriptions,
    }));

    setCategoryType(type);
  };

  // update categoryType quick-filter state when selectedCategories change
  useEffect(() => {
    if (!hasUrbanOrRuralCategories) {
      return;
    }
    const group = CATEGORY_GROUPS.find((g) => g.key === selectedGroup);

    // ONLY enabled categories
    const cats = (group?.categories ?? []).filter((c) => dataCategories.includes(c.description));

    const selected = new Set(selectedCategories);

    const urbanCats = cats.filter((c) => c.streetFamily === 'Urban');
    const ruralCats = cats.filter((c) => c.streetFamily === 'Rural');

    const allSelected = cats.length > 0 && cats.every((c) => selected.has(c.description));
    const allUrbanSelected =
    urbanCats.length > 0 &&
    urbanCats.every((c) => selected.has(c.description)) &&
    ruralCats.every((c) => !selected.has(c.description));
    const allRuralSelected =
    ruralCats.length > 0 &&
    ruralCats.every((c) => selected.has(c.description)) &&
    urbanCats.every((c) => !selected.has(c.description));

    if (allSelected) {
      setCategoryType('all');
    } else if (allUrbanSelected) {
      setCategoryType('urban');
    } else if (allRuralSelected) {
      setCategoryType('rural');
    } else {
      setCategoryType('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategories, selectedGroup, dataCategories]);

  // If no generalTraffic or freight categories, default to streetCategory group
  const hasGeneralTrafficCategories = rawData?.data?.some((x) => x.classification === 'generalTraffic');
  const hasFreightCategories = rawData?.data?.some((x) => x.classification === 'freight');
  useEffect(() => {
    if (
      hasGeneralTrafficCategories === undefined ||
      hasFreightCategories === undefined
    ) {
      return;
    }

    if (!hasGeneralTrafficCategories || !hasFreightCategories) {
      setSelectedGroup('streetCategory');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasGeneralTrafficCategories, hasFreightCategories]);

  // filter rawData based on selected categories and street family
  const filteredData = useMemo(() => {
    if (isEmpty(rawData)) return {};

    const legacyReport = !hasGeneralTrafficCategories && !hasFreightCategories;

    return {
      ...rawData,
      data: [...rawData.data]
        .sort((a, b) => a.rank - b.rank)
        .filter((x) => legacyReport || x.classification === selectedGroup)
        .filter((x) => dataCategories.includes(x.category))
        .filter((x) => selectedCategories.includes(x.category))
        .filter((x) => {
          if (!hasUrbanAndRuralToggles) return true;
          return x.streetFamily === selectedStreetFamily;
        }),
    };
  }, [
    rawData,
    selectedCategories,
    dataCategories,
    selectedStreetFamily,
    hasUrbanAndRuralToggles,
    selectedGroup,
    hasGeneralTrafficCategories,
    hasFreightCategories,
  ]);

  // callback for network selector
  const handleApplyCompareNetworks = (val) => {
    const networks = val.filter((x) => typeof x === 'number');
    const hno = val.filter((x) => typeof x === 'object');
    setParams({ ...params, networks, hno });
  };

  // set isComparativeReport based on params
  // this is so we can set the Comparative tab active on load
  const setIsComparativeReport = useSetRecoilState(isComparativeReportState);
  useEffect(() => {
    const isComparative = params.hno.length > 0 || params.networks.length > 0;
    setIsComparativeReport(isComparative);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.hno.length, params.networks.length]);

  // toggles: reset when report changes
  useEffect(() => {
    if (!toggles?.length) {
      setSelectedToggleOption({});
      return;
    }

    setSelectedToggleOption((prev) => {
      const next = Object.fromEntries(toggles.map((x) => [x.key, x.default]));
      return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, toggles, rcaId]);

  const handleToggleOption = (key, val, isMulti) => {
    setSelectedToggleOption((prevState) => ({ ...prevState, [key]: isMulti ? val : [val] }));
  };

  const handleApplyHnoFilter = (val) => {
    setParams((prev) => {
      if (val === null) {
        // eslint-disable-next-line no-unused-vars
        const { stateHighwaysHno, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        stateHighwaysHno: val,
      };
    });
  };

  // if loading or no rawData, show spinner / loader
  if (isLoading || isLoadingReport || isEmpty(rawData)) {
    return (
      <MainLayout>
        <LoadingSpinner />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Container maxW="full">
        <Box
          py={2}
          position="sticky"
          top="80px"
          background="gray.50"
          zIndex="200"
        >
          <Flex align="center" justify="space-between">
            <ReportHeading reportId={reportId}>
              <Heading fontSize="3xl" m={0}>
                {rawData.reportName}
              </Heading>
            </ReportHeading>
            <Flex align="center">
              <Pagination currentReportId={reportId} reportData={rawData} />
              <MoreInfoDrawer title="More Information" size="xl">
                <Accordion allowToggle mb={6}>
                  {relevantLinks?.length > 0 && (
                  <AccordionItem>
                    <AccordionButton>
                      <Text
                        flex="1"
                        textAlign="left"
                        fontWeight="bold"
                      >
                        Relevant Links
                      </Text>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4}>
                      <List spacing={1}>
                        {relevantLinks.map((link, idx) => (
                          <ListItem key={`link_${idx}`}>
                            <Link
                              as={RouterLink}
                              to={link.url}
                              fontSize="md"
                              key={link.url}
                              display="block"
                            >
                              {link.title}
                            </Link>
                          </ListItem>
                        ))}
                      </List>
                    </AccordionPanel>
                  </AccordionItem>
                  )}
                  {importantLinks?.length > 0 && (
                    <AccordionItem>
                      <AccordionButton>
                        <Text
                          flex="1"
                          textAlign="left"
                          fontWeight="bold"
                        >
                          Important Links
                        </Text>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <List spacing={1}>
                          {importantLinks.map((link, idx) => (
                            <ListItem key={`link_${idx}`}>
                              <Link href={link.url} target={link.isExternal ? '_blank' : '_self'}>
                                {link.title}
                              </Link>
                            </ListItem>
                          ))}
                        </List>
                      </AccordionPanel>
                    </AccordionItem>
                  )}
                  <AccordionItem>
                    <AccordionButton>
                      <Text
                        flex="1"
                        textAlign="left"
                        fontWeight="bold"
                      >
                        Measure Description
                      </Text>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4}>
                      <MeasureDescription reportId={reportId} />
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
                {reportId === 'ia1' && (
                  <CcdcCoverageIndicator />
                )}
                {rawData.rcas?.filter((rca) => rca.id === parseInt(rcaId, 10)).length === 1 && rawData.rcas?.filter((rca) => rca.dataConfidence).length === 1 && (
                  <ReportModal title="Data Quality" size="full" mt={4}>
                    <DataQualityTable reportId={reportId} />
                  </ReportModal>
                )}
                {Number.parseInt(rcaId, 10) < 1000 && rawData.rcas?.some((rca) => rca.dataConfidence) && (
                  <Box>
                    <Heading as="h3" size="sm" my={4}>Data Confidence</Heading>
                    <SimpleGrid minChildWidth={['100%', '45%']} spacing={2}>
                      {rawData.rcas?.filter((x) => x.dataConfidence).map((x) => (
                        <GridItem key={`rca_${x.rcaName}`} p={2} background="gray.50" borderRadius="md">
                          <Flex alignSelf="flex-start">
                            <Flex direction="row" alignItems="center">
                              <GaugeChart
                                nrOfLevels={4}
                                arcPadding={0.025}
                                cornerRadius={2}
                                percent={x.dataConfidence / 100}
                                colors={['#cccccc', '#999999', '#666666', '#333333']}
                                hideText
                                style={{ width: 150 }}
                              />
                              <Box>
                                <Text fontWeight="bold" fontSize="sm" lineHeight="1" mb={1}>{x.rcaName}</Text>
                                <Text fontWeight="normal" fontSize="sm">
                                  {x.dataConfidence}
                                  %
                                </Text>
                              </Box>
                            </Flex>
                          </Flex>
                        </GridItem>
                      ))}
                    </SimpleGrid>
                  </Box>
                )}
              </MoreInfoDrawer>
            </Flex>
          </Flex>
        </Box>

        <Box
          id="onf-filters-container"
          position="sticky"
          top="150px"
          background="gray.50"
          zIndex="350"
        >
          <Box background="white" p={4} borderRadius="md" boxShadow="base">
            {(rcaId === 81 && showOnfSHFilter === 'true') && (
            <Box
              mb={3}
              pb={3}
              borderBottom="1px"
              borderColor="gray.100"
            >
              <Text fontWeight="bold" fontSize="sm" mb={2}>State Highway Filter</Text>
              <HnoFilter
                selectedHno={(hno) => handleApplyHnoFilter(hno)}
                data={selectorData?.hnos}
                isLoading={isLoadingSelectorData}
                activeHnoFilter={rawData.reportParams?.stateHighwaysHno}
                buttonLabel="Apply"
              />
            </Box>
            )}
            <Stack
              direction={{ base: 'column', md: 'row' }}
              align={{ base: 'flex-start', md: 'center' }}
              justify="flex-start"
              spacing={4}
            >
              {toggles?.length > 0 && toggles.map((toggle) => (
                <ReportToggles
                  key={toggle.key}
                  options={toggle.options}
                  text={toggle.text}
                  isMulti={toggle.isMulti}
                  onSelectToggle={(value) => handleToggleOption(toggle.key, value, toggle.isMulti)}
                />
              ))}
              <HStack direction="row" spacing={3} align="flex-end">
                <VStack spacing={1} align="start">
                  <Text fontSize="xs" color="gray.600" textTransform="uppercase" fontWeight="bold">Classification:</Text>
                  <Menu>
                    <MenuButton
                      as={Button}
                      size="sm"
                      variant="outline"
                      colorScheme="gray"
                      rightIcon={<FiChevronDown />}
                    >
                      {CATEGORY_GROUPS.find((g) => g.key === selectedGroup)?.group ?? 'Select'}
                    </MenuButton>
                    <MenuList>
                      <MenuOptionGroup
                        type="radio"
                        value={selectedGroup}
                        onChange={(val) => setSelectedGroup(val)}
                      >
                        {CATEGORY_GROUPS.map((g) => {
                          const isDisabled = !(
                            reportClassifications.length > 0
                              ? reportClassifications
                              : ['streetCategory']
                          ).includes(g.key);

                          return (
                            <MenuItemOption
                              key={g.key}
                              value={g.key}
                              isDisabled={isDisabled}
                            >
                              <Tooltip
                                hasArrow
                                label="No data available for the network"
                                isDisabled={!isDisabled}
                              >
                                {g.group}
                              </Tooltip>
                            </MenuItemOption>
                          );
                        })}
                      </MenuOptionGroup>
                    </MenuList>
                  </Menu>
                </VStack>
                {hasUrbanOrRuralCategories && (
                <Stack direction="row" spacing={3} align="center" pb={2}>
                  <Switch
                    value="all"
                    onChange={() => applyQuickFilter('all')}
                    isChecked={categoryType === 'all'}
                    size="sm"
                    fontSize="sm"
                  >
                    All Categories
                  </Switch>
                  <Switch
                    value="urban"
                    onChange={() => applyQuickFilter('urban')}
                    isChecked={categoryType === 'urban'}
                    size="sm"
                    fontSize="sm"
                  >
                    Urban
                  </Switch>
                  <Switch
                    value="rural"
                    onChange={() => applyQuickFilter('rural')}
                    isChecked={categoryType === 'rural'}
                    size="sm"
                    fontSize="sm"
                  >
                    Rural
                  </Switch>
                </Stack>
                )}
                {hasUrbanAndRuralToggles && (
                <Stack direction="row" spacing={3} align="center" pb={1}>
                  <Text fontWeight="normal" fontSize="xs" mr={2} textTransform="uppercase">Street family: </Text>
                  <RadioGroup
                    onChange={(val) => setSelectedStreetFamily(val)}
                    value={selectedStreetFamily || ''}
                  >
                    <Stack direction="row" spacing={4}>
                      {[{ label: 'Both', value: 'B' }, { label: 'Urban', value: 'U' }, { label: 'Rural', value: 'R' }].map((x) => (
                        <Radio key={x.value} value={x.value} size="sm">
                          {x.label}
                        </Radio>
                      ))}
                    </Stack>
                  </RadioGroup>
                </Stack>
                )}
              </HStack>
            </Stack>

            {/* Category tags for the selected group only */}
            {selectedGroup && (
            <Box mt={2}>
              <Flex wrap="wrap">
                {CATEGORY_GROUPS.find((g) => g.key === selectedGroup).categories.map((cat) => {
                  const isDisabled = isCatDisabled(cat);
                  const isActive = selectedCategories.includes(cat.description);

                  return (
                    <Tooltip
                      hasArrow
                      key={cat.description}
                      label="Not used by the network"
                      isDisabled={!isDisabled}
                    >
                      <Tag
                        my={1}
                        mr={2}
                        size="sm"
                        fontSize="xs"
                        fontWeight="bold"
                        borderRadius="full"
                        variant="solid"
                        cursor={isDisabled ? 'not-allowed' : 'pointer'}
                        onClick={() => !isDisabled && handleSetCategory(selectedGroup, cat)}
                        opacity={(isActive && !isDisabled) ? 1 : 0.4}
                        bg={isDisabled ? 'gray.200' : theme.colors.onfCategory?.[cat.description]?.base || 'blue.600'}
                        color={isDisabled ? 'gray.600' : getContrastColor(theme.colors.onfCategory?.[cat.description]?.base || '#3182ce')}
                      >
                        {cat.description}
                      </Tag>
                    </Tooltip>
                  );
                })}
              </Flex>
            </Box>
            )}
          </Box>
        </Box>

        {isEmpty(rawData?.data) && (
          <EmptyState title="No Data" message="Sorry there is no data available for selected network and year" />
        )}

        {!isEmpty(rawData?.data) && (
          <>
            {/* Report selector and results: use union-of-selected categories */}
            {anySelected && filteredData.data?.length > 0 && (
              <>
                <ReportSelector
                  reportId={reportId}
                  selectedToggleOption={selectedToggleOption}
                  reportData={filteredData}
                  dataCategories={dataCategories?.filter((x) => selectedCategories.includes(x))}
                  networkSelector={(
                    <NetworkSelector
                      currentNetworks={rawData.reportParams.networks.length > 0
                        ? rawData.reportParams.networks.map((x) => parseInt(x, 10))
                        : rawData.rcas.filter((x) => x.id !== parseInt(rcaId, 10)).map((x) => x.id)}
                      currentHnos={rawData.reportParams.hno.length > 0 ? rawData.reportParams.hno : []}
                      lockYear={rawData.lockYear}
                      data={selectorData}
                      isLoading={isLoadingSelectorData || isLoadingReport}
                      callback={(val) => handleApplyCompareNetworks(val)}
                    />
                  )}
                />
                <Text mt={4} fontSize="sm">
                  Regional and National results include Local Authorities and State Highways.
                  <br />
                  Local Authority results and Peer Group results do not include State Highways.
                </Text>
              </>
            )}

            {/* No data for selected categories */}
            {anySelected && filteredData.data?.length === 0 && !isLoadingReport && (
              <EmptyState title="No Data" message="Sorry there is no data available for selected categories" />
            )}

            {/* No category selected */}
            {!anySelected && filteredData.data?.length === 0 && !isLoadingReport && (
              <Box my={4} py={8} textAlign="center" borderRadius="md" border="1px solid">
                <Text>Please select at least 1 category</Text>
              </Box>
            )}
          </>
        )}
      </Container>
    </MainLayout>
  );
}
